import { Ability } from '@casl/ability';
import AddIcon from '@mui/icons-material/Add';
import {
  Actions,
  LocationForm,
  ResourceForm,
  Subjects,
} from '@dispatch/shared/types';
import Close from '@mui/icons-material/Close';
import {
  Button,
  Chip,
  Grid,
  IconButton,
  List,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { ConfirmModal, Section } from '@dispatch/web/ui';

export type LocationSidebarProps = {
  selected: number;
  setSelected: (selected: number) => void;
  currentLocation: number;
  setLocation: (currentLocation: number) => void;
  permissions: Ability<[Actions, Subjects]> | undefined;
  watch: UseFormWatch<ResourceForm>;
  onDeleteLocation: (index: number) => void;
  resourceFormIsDirty: boolean;
  resourceSetValue: UseFormSetValue<ResourceForm>;
  addLocation: () => void;
  isReadOnly?: boolean;
};

export function LocationSidebar({
  selected,
  setSelected,
  currentLocation,
  setLocation,
  permissions,
  watch,
  onDeleteLocation,
  resourceFormIsDirty,
  resourceSetValue,
  addLocation,
  isReadOnly = false,
}: LocationSidebarProps) {
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');

  const [toDelete, setToDelete] = useState<number | null>(null);

  const locations = watch('locations');

  const deleteLocation = (index: number) => {
    if (
      currentLocation > index ||
      (locations && currentLocation === locations.length - 1)
    ) {
      selectLocation(currentLocation - 1);
    }
  };

  const selectLocation = useCallback(
    (index: number) => {
      setLocation(index);
      setSelected(index);
    },
    [setLocation, setSelected]
  );

  const startOfDay = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const isLocationActive = (location: LocationForm) => {
    const currentDate = startOfDay(new Date());
    const startDate = location?.serviceInformation?.startDate;
    const endDate = location?.serviceInformation?.endDate;

    if (!startDate) return false;

    const start = startOfDay(new Date(startDate));

    // If no end date, active if start date has passed
    if (!endDate) {
      return start <= currentDate;
    }

    // If has end date, active if within the date range (end date inclusive)
    const end = startOfDay(new Date(endDate));
    return start <= currentDate && end >= currentDate;
  };

  const isLocationInactive = (location: LocationForm) => {
    const currentDate = startOfDay(new Date());
    const endDate = location?.serviceInformation?.endDate;
    return endDate && startOfDay(new Date(endDate)) < currentDate;
  };

  const renderLocationsList = useCallback(() => {
    return locations?.map((location: LocationForm, index: number) => {
      const isActive = isLocationActive(location);
      const isInactive = isLocationInactive(location);
      const isVirtual = JSON.parse(
        location?.locationInformation?.isVirtualLocation ?? 'false'
      );
      const isStarting = location?.serviceInformation?.isStartingLocation;

      return (
        <MenuItem
          key={index}
          selected={selected === index}
          onClick={() => selectLocation(index)}
          sx={{
            padding: '10px 5px',
            borderRadius: 1,
            position: 'relative',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&:hover .delete-button': {
              opacity: 1,
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            },
          }}
        >
          {hasUpsertPermissions && (locations?.length || 0) > 1 && (
            <IconButton
              className="delete-button"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setToDelete(index);
              }}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 4,
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s',
                p: 0.5,
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                },
              }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ width: '100%', pr: 3 }}
          >
            <Tooltip
              title={
                (location?.locationInformation?.name?.length || 0) > 13
                  ? location?.locationInformation?.name || ''
                  : ''
              }
              arrow
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: selected === index ? 600 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  flex: 1,
                  minWidth: 0,
                }}
                data-testid={`location_${location?.locationInformation?.name}`}
              >
                {location?.locationInformation?.name}
              </Typography>
            </Tooltip>

            {(isVirtual || isStarting || isActive || isInactive) && (
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                {isVirtual && (
                  <Chip
                    label="Virtual"
                    size="small"
                    color="info"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 0.75,
                      },
                    }}
                  />
                )}

                {isStarting && (
                  <Chip
                    label="Home"
                    size="small"
                    color="primary"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 0.75,
                      },
                    }}
                  />
                )}

                {isActive ? (
                  <Chip
                    label="Active"
                    size="small"
                    color="success"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 0.75,
                      },
                    }}
                  />
                ) : isInactive ? (
                  <Chip
                    label="Expired"
                    size="small"
                    color="error"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 0.75,
                      },
                    }}
                  />
                ) : null}
              </Stack>
            )}
          </Stack>
        </MenuItem>
      );
    });
  }, [hasUpsertPermissions, locations, selectLocation, selected]);

  const onDeleteConfirm = () => {
    if (toDelete != null) {
      if (!resourceFormIsDirty) {
        onDeleteLocation(toDelete);
      } else {
        locations.splice(toDelete, 1);
        resourceSetValue('locations', locations);
      }
      deleteLocation(toDelete);
      setToDelete(null);
    }
  };

  return (
    <Section
      title="Locations"
      rightTopControl={
        !isReadOnly && hasUpsertPermissions
          ? () => (
              <Button
                variant="text"
                size="small"
                data-testid="add-location"
                sx={{
                  fontSize: '10px',
                  margin: 0,
                  padding: 0,
                }}
                onClick={() => {
                  addLocation();
                }}
              >
                <AddIcon sx={{ fontSize: '10px' }} /> Add Location
              </Button>
            )
          : undefined
      }
    >
      <Grid item>
        <List>{renderLocationsList()}</List>
        {toDelete != null && toDelete >= 0 && (
          <ConfirmModal
            onConfirm={onDeleteConfirm}
            onDeny={() => setToDelete(null)}
            confirmContent="Are you sure you want to delete this location?"
            confirmTitle="Delete Confirmation"
            modalType="delete"
          />
        )}
      </Grid>
    </Section>
  );
}
